import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UserRim } from "@/lib/db/schema";
import { useSession } from "@/lib/auth/client";
import { toast } from "sonner";

interface UserRimWithCount extends UserRim {
  generationsCount: number;
}

interface UserRimsResponse {
  personal: UserRimWithCount[];
  company: UserRimWithCount[];
}

export function useUserRims() {
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;

  return useQuery<UserRimsResponse>({
    queryKey: ["userRims"],
    queryFn: async () => {
      const response = await fetch("/api/rims/user");
      if (!response.ok) {
        throw new Error("Failed to fetch user rims");
      }
      return response.json();
    },
    enabled: isAuthenticated, // Only fetch when user is authenticated
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    retry: 1,
  });
}

interface CreateUserRimParams {
  name: string;
  imageUrl: string;
  companyId?: string | null; // null for explicit personal uploads, undefined for auto-detect
  sku?: string;
  brand?: string;
  style?: string;
}

export function useCreateUserRim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateUserRimParams) => {
      const response = await fetch("/api/rims/user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if (!response.ok) {
        throw new Error("Failed to create user rim");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userRims"] });
    },
  });
}

export function useDeleteUserRim() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (rimId: string) => {
      const response = await fetch(`/api/rims/user?rimId=${rimId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete rim");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userRims"] });
      queryClient.invalidateQueries({ queryKey: ["generations"] });
      toast.success("Wheel deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
